import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Trash2, Loader2, Bot } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";
import {
  listAIModels,
  deleteAIModel,
  listAIProviders,
  toggleAIModelIntegration,
} from "@/services/aiModelsService";
import type {
  AIModel as APIAIModel,
  AIProvider,
} from "@/types/aiModels";
import { AIModelForm } from "./AIModelForm";
import { Switch } from "@/components/ui/switch";

interface ModelManagementProps {
  onBack: () => void;
}

type ViewMode = "list" | "form";

export const ModelManagement = ({ onBack }: ModelManagementProps) => {
  const [models, setModels] = useState<APIAIModel[]>([]);
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [editingModel, setEditingModel] = useState<APIAIModel | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();

  const loadModels = async () => {
    setLoading(true);
    try {
      const [modelsRes, providersRes] = await Promise.all([
        listAIModels({ limit: 100 }),
        listAIProviders(),
      ]);

      if (modelsRes.success && modelsRes.data) {
        setModels(modelsRes.data.models);
      }
      if (providersRes.success && providersRes.data) {
        setProviders(providersRes.data);
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModels();
  }, []);

  const handleSaveModel = async () => {
    await loadModels();
    setViewMode("list");
    setEditingModel(null);
  };

  const handleNewModel = () => {
    setEditingModel(null);
    setViewMode("form");
  };

  const handleEditModel = (model: APIAIModel) => {
    setEditingModel(model);
    setViewMode("form");
  };

  if (viewMode === "form") {
    return (
      <AIModelForm
        onBack={() => setViewMode("list")}
        onSave={handleSaveModel}
        editingModel={editingModel}
      />
    );
  }

  const handleDeleteModel = async (id: string, name: string) => {
    if (!canDelete("models")) {
      toast({
        title: "Sem permissão",
        description: "Você não tem permissão para gerenciar modelos",
        variant: "destructive",
      });
      return;
    }

    if (!confirm(`Tem certeza que deseja remover o modelo "${name}"?`)) {
      return;
    }

    try {
      const response = await deleteAIModel(id);

      if (response.success) {
        toast({
          title: "Modelo removido",
          description: "Modelo removido com sucesso",
        });
        await loadModels();
      } else {
        toast({
          title: "Erro ao remover modelo",
          description: response.error?.message || "Erro desconhecido",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao remover modelo",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const handleToggleIntegration = async (
    model: APIAIModel,
    isIntegrationActive: boolean
  ) => {
    if (!canUpdate("models")) {
      toast({
        title: "Sem permissão",
        description: "Você não tem permissão para gerenciar modelos",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await toggleAIModelIntegration(
        model.id,
        isIntegrationActive
      );

      if (response.success && response.data) {
        setModels((prev) =>
          prev.map((item) =>
            item.id === model.id
              ? { ...item, is_integration_active: isIntegrationActive }
              : item
          )
        );
        toast({
          title: isIntegrationActive
            ? "Integração ativada"
            : "Integração desativada",
          description: `A integração do modelo "${model.name}" foi ${
            isIntegrationActive ? "ativada" : "desativada"
          }.`,
        });
      } else {
        toast({
          title: "Erro ao alterar integração",
          description: response.error?.message || "Erro desconhecido",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao alterar integração",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };


  const getProviderColor = (providerName: string) => {
    const colors: Record<string, string> = {
      OpenAI: "bg-green-500/10 text-green-600",
      Anthropic: "bg-purple-500/10 text-purple-600",
      Gemini: "bg-blue-500/10 text-blue-600",
      Google: "bg-blue-500/10 text-blue-600",
      Grok: "bg-orange-500/10 text-orange-600",
      Outro: "bg-gray-500/10 text-gray-600",
    };
    return colors[providerName] || colors.Outro;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando modelos...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard
      permission="res_models.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>Você não tem permissão para visualizar modelos.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Bot}
        title="Modelos de IA"
        description="Configure modelos e seus custos de token"
        onBack={onBack}
        actions={
          canCreate("models") && (
            <Button
              className="bg-gradient-primary text-white hover:opacity-90"
              onClick={handleNewModel}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Modelo
            </Button>
          )
        }
      />

      <main className="container mx-auto px-6 py-6">
        <Card>
          <CardHeader>
            <CardTitle>Modelos Configurados</CardTitle>
            <CardDescription>
              Lista de todos os modelos de IA configurados e suas regras de
              preço
            </CardDescription>
          </CardHeader>
          <CardContent>
            {models.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Bot className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    Nenhum modelo cadastrado
                  </p>
                  {canCreate("models") && (
                    <Button onClick={handleNewModel} variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Criar Primeiro Modelo
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Provedor</TableHead>
                    <TableHead>Identificador</TableHead>
                    <TableHead>Faixas de Preço</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Integração</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const filteredModels = models.filter((model) => {
                      const provider = providers.find(
                        (p) => p.id === model.provider_id
                      );
                      return provider?.is_active === true;
                    });

                    if (filteredModels.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8">
                            Nenhum modelo encontrado
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return filteredModels.map((model) => (
                      <TableRow key={model.id}>
                        <TableCell className="font-medium">
                          {model.name}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={getProviderColor(
                              model.provider_name || "Outro"
                            )}
                          >
                            {model.provider_name || "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {model.model_identifier}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {model.pricing_tiers?.length || 0} faixa
                            {(model.pricing_tiers?.length || 0) !== 1
                              ? "s"
                              : ""}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={model.is_active ? "secondary" : "outline"}
                          >
                            {model.is_active ? "Ativo" : "Inativo"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={!!model.is_integration_active}
                              onCheckedChange={(checked) =>
                                handleToggleIntegration(model, checked)
                              }
                              disabled={!canUpdate("models")}
                              aria-label={`Alternar integração do modelo ${model.name}`}
                            />
                            <span className="text-sm text-muted-foreground">
                              {model.is_integration_active ? "Ativa" : "Inativa"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditModel(model)}
                              disabled={!canUpdate("models")}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleDeleteModel(model.id, model.name)
                              }
                              disabled={!canDelete("models")}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ));
                })()}
              </TableBody>
            </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
    </PermissionGuard>
  );
};
